import React, { ReactNode } from 'react';
import styled from 'styled-components';
import constants, { fontWeight } from '../../common/theme/constants';
import colors from '../../common/theme/colors';

const { fontSize, spacing } = constants;

// ------------------
// KitForm.FieldSet
// ------------------

const StyledFieldset = styled.fieldset`
  padding: ${constants.spacing.absolute.s}px;
  padding-bottom: 0px;
  & > div.kit-form-group,
  & .kit-flex-col > div.kit-form-group {
    margin-bottom: ${constants.spacing.absolute.l}px;
  }
  & > div.kit-form-group:last-child,
  & .kit-flex-col:last-child > div.kit-form-group {
    margin-bottom: 0;
  }
`;

export const StyledLegend = styled.legend<{
  required?: boolean;
  error?: string;
}>`
  color: ${({ error, theme }) => (error ? theme.error : theme.form.text)};
  font-size: ${constants.fontSize.text_14}rem;
  font-weight: ${constants.fontWeight.bold};
  margin-left: -${constants.spacing.absolute.s}px;

  ${({ required }) =>
    required &&
    `
    &:after {
      content: ' *';
    }
  `}
`;

export interface KitFormFieldSetProps extends React.ComponentPropsWithoutRef<'div'> {
  className?: string;
  legend: string;
  children: ReactNode;
  required?: boolean;
}

export const KitFormFieldSet: React.FC<KitFormFieldSetProps> = ({
  className,
  legend,
  children,
  required = false,
}) => {
  const allClassNames = `${className ?? ''} kit-form-fieldset`;
  return (
    <StyledFieldset className={allClassNames}>
      <StyledLegend required={required}>{legend}</StyledLegend>
      {children}
    </StyledFieldset>
  );
};

// ------------------
// KitForm.Group
// ------------------

export interface KitFormGroupProps extends React.ComponentPropsWithoutRef<'div'> {
  className?: string;
  children: ReactNode;
}

export const KitFormGroup: React.FC<KitFormGroupProps> = ({ className, children }) => {
  const allClassNames = `${className ?? ''} kit-form-group`;
  return <div className={allClassNames}>{children}</div>;
};

// ------------------
// KitForm.Label
// ------------------

const FormLabel = styled.label<{
  focused?: boolean;
  required?: boolean;
}>`
  background-color: inherit;
  color: ${({ theme }) => theme.label};
  display: block;
  font-size: ${fontSize.text_14}rem;
  font-weight: ${({ focused }) => (focused ? fontWeight.bold : fontWeight.normal)};
  margin-bottom: ${spacing.absolute.xs}px;

  ${({ required }) =>
    required &&
    `
    &:after {
      content: ' *';
    }
  `}
`;

export interface KitFormLabelProps extends React.ComponentPropsWithoutRef<'label'> {
  children?: ReactNode;
  className?: string;
  focused?: boolean;
  htmlFor: string;
  text?: string;
  required?: boolean;
  as?: 'label' | 'h4';
}

export const KitFormLabel: React.FC<KitFormLabelProps> = ({
  children,
  className,
  focused,
  htmlFor,
  text,
  required,
  as,
  ...rest
}: KitFormLabelProps) => {
  const props = {
    as,
    className,
    focused,
    required,
    ...rest,
  };
  const labelProps = { ...props, htmlFor };
  const elementProps = as === 'h4' ? props : labelProps;

  return (
    <FormLabel {...elementProps}>
      {text}
      {children}
    </FormLabel>
  );
};

// ------------------
// KitForm.Value
// ------------------

const FormValue = styled.div`
  color: ${colors.black};
  font-size: ${fontSize.text_14}rem;
`;

export interface KitFormValueProps extends React.ComponentPropsWithoutRef<'div'> {
  id?: string;
  dataQaId?: string;
  className?: string;
  children: ReactNode;
}

export const KitFormValue: React.FC<KitFormValueProps> = ({
  id,
  dataQaId,
  className,
  children,
}) => {
  return (
    <FormValue className={className} id={id} data-qa-id={dataQaId}>
      {children}
    </FormValue>
  );
};

// ------------------
// KitForm.InfoText
// ------------------

const FormInfoText = styled.div<{ isError?: boolean }>`
  color: ${({ isError, theme }) => (isError ? theme.error : theme.label)};
  font-size: ${fontSize.text_12}rem;
  margin-top: ${spacing.absolute.xs}px;
  line-height: 1;
  min-height: 1rem;
`;

export interface KitFormInfoTextProps extends React.ComponentPropsWithoutRef<'div'> {
  className?: string;
  isError?: boolean;
  text: string;
}

export const KitFormInfoText: React.FC<KitFormInfoTextProps> = ({ text, isError, ...rest }) => {
  return (
    <FormInfoText isError={isError} {...rest}>
      {text}
    </FormInfoText>
  );
};
